import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Search, 
  Filter, 
  Plus, 
  Eye, 
  Edit, 
  Trash2, 
  CheckCircle, 
  Clock, 
  AlertCircle,
  User,
  Calendar,
  MapPin,
  MessageSquare,
  MoreVertical,
  Download,
  RefreshCw,
  MessageCircle
} from 'lucide-react';
import { useAuth } from '@/store/hooks';
import { downloadComplaintsExcel } from '@/services/exportService';
import { getComplaintPlotLabel } from '@/utils/complaintUtils';
import { mobileClasses, isMobile } from '@/utils/mobileUtils';
import MobileComplaintsTable from '@/components/complaints/MobileComplaintsTable';

interface Complaint {
  id: string;
  complaint_number?: string;
  title: string;
  description: string;
  status: 'pending' | 'assigned' | 'in_progress' | 'on_hold' | 'resolved' | 'closed';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  category: {
    id: string;
    name: string;
  };
  user: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
    block_number?: string;
    flat_number?: string;
  };
  department?: {
    id: string;
    name: string;
  };
  assigned_to?: {
    id: string;
    first_name: string;
    last_name: string;
  };
  location?: string;
  block_number?: string;
  flat_number?: string;
  created_at: string;
  updated_at: string;
  resolution_notes?: string;
}

interface ComplaintStats {
  total: number;
  pending: number;
  assigned: number;
  in_progress: number;
  on_hold: number;
  resolved: number;
  closed: number;
}

const ComplaintsPage = () => {
  const { user } = useAuth();
  const [complaints, setComplaints] = useState<Complaint[]>([]);
  const [filteredComplaints, setFilteredComplaints] = useState<Complaint[]>([]);
  const [stats, setStats] = useState<ComplaintStats>({
    total: 0,
    pending: 0,
    assigned: 0,
    in_progress: 0,
    on_hold: 0,
    resolved: 0,
    closed: 0
  });
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedComplaint, setSelectedComplaint] = useState<Complaint | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [itemsPerPage] = useState(10);
  const [isExporting, setIsExporting] = useState(false);
  const [updatingStatusId, setUpdatingStatusId] = useState<string | null>(null);
  const [statusUpdateError, setStatusUpdateError] = useState<string | null>(null);
  
  // Role-based view modes
  const [viewMode, setViewMode] = useState<'all' | 'tasks' | 'my-complaints' | 'department' | 'member'>('all');
  
  // Role checks
  const isUniversalAdmin = user?.role === 'universal_admin';
  const isOfficeAdmin = user?.role === 'office_admin';
  const isAdmin = isUniversalAdmin || isOfficeAdmin;
  const isDepartmentHead = user?.role === 'department_head';
  const isFrontWorker = user?.role === 'front_worker';
  const isDepartmentStaff = isDepartmentHead || isFrontWorker;
  const isMember = user?.role === 'member';
  const isCommitteeMember = user?.role === 'committee_member';
  
  // Get view mode from URL params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const view = params.get('view') as 'all' | 'tasks' | 'my-complaints' | 'department' | 'member' | null;
    if (view) {
      setViewMode(view);
    } else if (isDepartmentStaff) {
      setViewMode('tasks');
    } else if (isMember) {
      setViewMode('my-complaints');
    } else if (isCommitteeMember) {
      setViewMode('department');
    }
  }, [isDepartmentStaff, isMember, isCommitteeMember]);

  // Fetch complaints (paginated list + full-dataset stats from API)
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchComplaints();
    }, searchTerm.trim() ? 400 : 0);
    return () => clearTimeout(timer);
  }, [currentPage, statusFilter, priorityFilter, viewMode, searchTerm, startDate, endDate]);

  useEffect(() => {
    setFilteredComplaints(complaints);
  }, [complaints]);

  const handleExportExcel = async () => {
    setIsExporting(true);
    try {
      await downloadComplaintsExcel({
        status: statusFilter !== 'all' ? statusFilter : undefined,
        priority: priorityFilter !== 'all' ? priorityFilter : undefined,
        search: searchTerm.trim() || undefined
      });
    } catch (error) {
      console.error('Export failed:', error);
      alert('Failed to export complaints to Excel');
    } finally {
      setIsExporting(false);
    }
  };

  const fetchComplaints = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: itemsPerPage.toString(),
        ...(statusFilter !== 'all' && { status: statusFilter }),
        ...(priorityFilter !== 'all' && { priority: priorityFilter }),
        ...(searchTerm.trim() && { search: searchTerm.trim() }),
        ...(startDate && { start_date: new Date(startDate).toISOString() }),
        ...(endDate && { end_date: new Date(endDate).toISOString() })
      });

      // Add view-specific filters
      if (viewMode === 'my-complaints' && (isMember || isCommitteeMember)) {
        // For my-complaints, backend already filters by user_id
        // No additional params needed
      } else if (viewMode === 'department' && isCommitteeMember) {
        // For department view, backend filters by department
        // No additional params needed
      }

      const response = await fetch(`/api/v1/complaints?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        const fetchedComplaints = data.data || [];
        
        // Additional client-side filtering for view modes
        let filtered = fetchedComplaints;
        
        if (viewMode === 'my-complaints' && isCommitteeMember) {
          // Show only complaints raised by the user
          filtered = fetchedComplaints.filter((c: Complaint) => 
            c.user?.id === user?.id
          );
        }
        
        setComplaints(filtered);

        // pagination.total is the full count; data array is only one page (e.g. 10 items)
        const paginationTotal =
          data.pagination?.total ??
          (typeof data.total === 'number' ? data.total : 0);
        const totalPagesFromApi =
          data.pagination?.totalPages ??
          (typeof data.totalPages === 'number'
            ? data.totalPages
            : Math.max(1, Math.ceil(paginationTotal / itemsPerPage)));

        setTotalCount(paginationTotal);
        setTotalPages(totalPagesFromApi);

        const apiStats = data.stats || {};
        setStats({
          total: Number(apiStats.total) || paginationTotal,
          pending: Number(apiStats.pending) || 0,
          assigned: Number(apiStats.assigned) || 0,
          in_progress: Number(apiStats.in_progress) || 0,
          on_hold: Number(apiStats.on_hold) || 0,
          resolved: Number(apiStats.resolved) || 0,
          closed: Number(apiStats.closed) || 0
        });
      }
    } catch (error) {
      console.error('Error fetching complaints:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const applyComplaintPatch = (complaintId: string, patch: Partial<Complaint>) => {
    const merge = (list: Complaint[]) =>
      list.map((c) => (c.id === complaintId ? { ...c, ...patch } : c));
    setComplaints(merge);
    setFilteredComplaints(merge);
  };

  const handleStatusChange = async (complaintId: string, newStatus: string) => {
    const previous = complaints.find((c) => c.id === complaintId);
    if (!previous || previous.status === newStatus) return;

    setStatusUpdateError(null);
    setUpdatingStatusId(complaintId);
    applyComplaintPatch(complaintId, { status: newStatus as Complaint['status'] });

    try {
      const response = await fetch(`/api/v1/complaints/${complaintId}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        const data = await response.json();
        const updated = data.data as Complaint | undefined;
        if (updated) {
          applyComplaintPatch(complaintId, {
            status: (updated.status || newStatus) as Complaint['status'],
            updated_at: updated.updated_at,
            complaint_number: updated.complaint_number ?? previous.complaint_number
          });
        }
        fetchComplaints();
      } else {
        applyComplaintPatch(complaintId, { status: previous.status });
        const errorData = await response.json().catch(() => ({}));
        const msg = errorData.message || 'Failed to update complaint status';
        setStatusUpdateError(msg);
      }
    } catch (error) {
      applyComplaintPatch(complaintId, { status: previous.status });
      setStatusUpdateError('Network error while updating status');
      console.error('Error updating complaint status:', error);
    } finally {
      setUpdatingStatusId(null);
    }
  };

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      pending: 'Pending',
      assigned: 'Assigned',
      in_progress: 'In Progress',
      on_hold: 'On Hold',
      resolved: 'Resolved',
      closed: 'Closed'
    };
    return labels[status] || status.replace(/_/g, ' ');
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'assigned': return 'bg-blue-100 text-blue-800';
      case 'in_progress': return 'bg-purple-100 text-purple-800';
      case 'on_hold': return 'bg-orange-100 text-orange-800';
      case 'resolved': return 'bg-green-100 text-green-800';
      case 'closed': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-800';
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const canManageStatus = isAdmin || isDepartmentStaff;
  const renderStatusControl = (complaint: Complaint) => {
    const isUpdating = updatingStatusId === complaint.id;
    if (canManageStatus) {
      return (
        <select
          value={complaint.status}
          disabled={isUpdating}
          onChange={(e) => handleStatusChange(complaint.id, e.target.value)}
          className={`text-xs font-semibold px-2.5 py-1.5 rounded-lg border border-gray-200 ${getStatusColor(complaint.status)} min-w-[8.5rem] focus:ring-2 focus:ring-blue-500 disabled:opacity-60`}
          aria-label={`Status for ${complaint.complaint_number || complaint.title}`}
        >
          <option value="pending">Pending</option>
          <option value="assigned">Assigned</option>
          <option value="in_progress">In Progress</option>
          <option value="on_hold">On Hold</option>
          <option value="resolved">Resolved</option>
          <option value="closed">Closed</option>
        </select>
      );
    }
    return (
      <span className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-lg ${getStatusColor(complaint.status)}`}>
        {getStatusLabel(complaint.status)}
      </span>
    );
  };

  const renderComplaintCard = (complaint: Complaint) => {
    const plotLabel = getComplaintPlotLabel(complaint);
    return (
    <div
      key={complaint.id}
      className={`border rounded-lg p-4 bg-white hover:shadow-md transition-shadow ${
        updatingStatusId === complaint.id ? 'ring-2 ring-blue-200' : 'border-gray-200'
      }`}
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            {complaint.complaint_number && (
              <span className="text-xs font-mono font-semibold text-blue-700 bg-blue-50 px-2 py-0.5 rounded">
                {complaint.complaint_number}
              </span>
            )}
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${getPriorityColor(complaint.priority)}`}>
              {complaint.priority.toUpperCase()}
            </span>
            {plotLabel && (
              <span className="text-xs font-medium text-amber-800 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded flex items-center gap-1">
                <MapPin className="h-3 w-3 shrink-0" />
                {plotLabel}
              </span>
            )}
          </div>
          <h4 className="text-sm font-semibold text-gray-900 line-clamp-1">{complaint.title}</h4>
          <p className="text-sm text-gray-500 line-clamp-2 mt-0.5">{complaint.description}</p>
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
            <span className="flex items-center gap-1">
              <User className="h-3.5 w-3.5" />
              {complaint.user
                ? `${complaint.user.first_name} ${complaint.user.last_name}`
                : 'Unknown'}
            </span>
            <span>{complaint.category?.name || '—'}</span>
            {complaint.department?.name && <span>{complaint.department.name}</span>}
            <span className="flex items-center gap-1">
              <Calendar className="h-3.5 w-3.5" />
              {formatDate(complaint.created_at)}
            </span>
          </div>
        </div>
        <div className="flex sm:flex-col items-center sm:items-end gap-2 shrink-0">
          {renderStatusControl(complaint)}
          {updatingStatusId === complaint.id && (
            <span className="text-xs text-blue-600 flex items-center gap-1">
              <RefreshCw className="h-3 w-3 animate-spin" />
              Saving…
            </span>
          )}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => {
                setSelectedComplaint(complaint);
                setShowDetailsModal(true);
              }}
              className="inline-flex items-center justify-center p-2 rounded-lg border border-gray-200 text-blue-600 hover:bg-blue-50"
              title="Quick view"
            >
              <Eye className="h-4 w-4" />
            </button>
            <Link
              to={`/app/complaints/${complaint.id}`}
              className="inline-flex items-center justify-center p-2 rounded-lg border border-gray-200 text-indigo-600 hover:bg-indigo-50"
              title="Open details"
            >
              <Edit className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
  };

  const getPageTitle = () => {
    if (viewMode === 'tasks') return 'My Tasks';
    if (viewMode === 'my-complaints') return 'My Complaints';
    if (viewMode === 'department') return 'Department Complaints';
    if (isAdmin) return 'All Complaints';
    if (isDepartmentStaff) return 'Department Complaints';
    if (isMember) return 'My Complaints';
    return 'Complaints';
  };

  const getPageDescription = () => {
    if (viewMode === 'tasks') return 'View and manage tasks assigned to your department';
    if (viewMode === 'my-complaints') return 'View complaints you have raised';
    if (viewMode === 'department') return 'View complaints assigned to your department';
    if (isAdmin) return 'Manage all complaints in the system';
    if (isDepartmentStaff) return 'View complaints assigned to your department';
    if (isMember) return 'View and track your complaint history';
    return 'View and manage complaints';
  };

  const statusTabs = [
    { value: 'all', label: 'All', count: stats.total },
    { value: 'pending', label: 'Pending', count: stats.pending },
    { value: 'assigned', label: 'Assigned', count: stats.assigned },
    { value: 'in_progress', label: 'In Progress', count: stats.in_progress },
    { value: 'on_hold', label: 'On Hold', count: stats.on_hold },
    { value: 'resolved', label: 'Resolved', count: stats.resolved },
    { value: 'closed', label: 'Closed', count: stats.closed }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className={`max-w-7xl mx-auto ${mobileClasses.container} py-6 sm:py-8`}>
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <div className={`${mobileClasses.flexMobileReverse} justify-between items-center`}>
            <div className={`${isMobile() ? 'mt-4' : ''}`}>
              <h1 className={`${mobileClasses.textMobileXLarge} font-bold text-gray-900`}>{getPageTitle()}</h1>
              <p className={`mt-2 ${mobileClasses.textMobile} text-gray-600`}>
                {getPageDescription()}
              </p>
            </div>
            <div className={`${mobileClasses.flexMobile} ${isMobile() ? 'space-y-2' : 'space-x-3'}`}>
              <button
                onClick={handleExportExcel}
                disabled={isExporting}
                className={`${mobileClasses.buttonMobile} border border-gray-300 rounded-md shadow-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50`}
              >
                <Download className={`h-4 w-4 ${isMobile() ? 'mr-1' : 'mr-2'}`} />
                <span className={isMobile() ? 'text-sm' : ''}>{isExporting ? 'Exporting…' : 'Excel'}</span>
              </button>
              <button
                onClick={fetchComplaints}
                className={`${mobileClasses.buttonMobile} border border-gray-300 rounded-md shadow-sm font-medium text-gray-700 bg-white hover:bg-gray-50`}
              >
                <RefreshCw className={`${isMobile() ? 'h-4 w-4' : 'h-4 w-4'} ${isMobile() ? 'mr-1' : 'mr-2'}`} />
                <span className={isMobile() ? 'text-sm' : ''}>Refresh</span>
              </button>
              {(isAdmin || isMember || isCommitteeMember) && (
                <Link
                  to="/app/complaints/new"
                  className={`${mobileClasses.buttonMobile} border border-transparent rounded-md shadow-sm font-medium text-white bg-blue-600 hover:bg-blue-700`}
                >
                  <Plus className={`${isMobile() ? 'h-4 w-4' : 'h-4 w-4'} ${isMobile() ? 'mr-1' : 'mr-2'}`} />
                  <span className={isMobile() ? 'text-sm' : ''}>New Complaint</span>
                </Link>
              )}
              {isMember && (
                <Link
                  to="/app/whatsapp-bot"
                  className={`${mobileClasses.buttonMobile} border border-green-300 rounded-md shadow-sm font-medium text-green-700 bg-green-50 hover:bg-green-100`}
                >
                  <MessageCircle className={`${isMobile() ? 'h-4 w-4' : 'h-4 w-4'} ${isMobile() ? 'mr-1' : 'mr-2'}`} />
                  <span className={isMobile() ? 'text-sm' : ''}>WhatsApp Bot</span>
                </Link>
              )}
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-5 mb-6">
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <MessageSquare className="h-6 w-6 text-gray-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Total</dt>
                    <dd className="text-lg font-medium text-gray-900">{stats.total}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Clock className="h-6 w-6 text-yellow-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Pending</dt>
                    <dd className="text-lg font-medium text-gray-900">{stats.pending}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <AlertCircle className="h-6 w-6 text-purple-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">In Progress</dt>
                    <dd className="text-lg font-medium text-gray-900">{stats.in_progress}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <CheckCircle className="h-6 w-6 text-green-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Resolved</dt>
                    <dd className="text-lg font-medium text-gray-900">{stats.resolved}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <CheckCircle className="h-6 w-6 text-gray-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Closed</dt>
                    <dd className="text-lg font-medium text-gray-900">{stats.closed}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* View Mode Switcher for Committee Members */}
        {isCommitteeMember && (
          <div className="bg-white shadow rounded-lg mb-6">
            <div className="p-4 border-b border-gray-200">
              <h3 className="text-sm font-medium text-gray-700 mb-3">View Mode</h3>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => {
                    setViewMode('my-complaints');
                    setCurrentPage(1);
                  }}
                  className={`px-4 py-2 rounded-md text-sm font-medium ${
                    viewMode === 'my-complaints'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  My Complaints
                </button>
                <button
                  onClick={() => {
                    setViewMode('department');
                    setCurrentPage(1);
                  }}
                  className={`px-4 py-2 rounded-md text-sm font-medium ${
                    viewMode === 'department'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Department Complaints
                </button>
                <button
                  onClick={() => {
                    setViewMode('member');
                    setCurrentPage(1);
                  }}
                  className={`px-4 py-2 rounded-md text-sm font-medium ${
                    viewMode === 'member'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Member View
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Task View Switcher for Department Staff */}
        {isDepartmentStaff && (
          <div className="bg-white shadow rounded-lg mb-6">
            <div className="p-4 border-b border-gray-200">
              <h3 className="text-sm font-medium text-gray-700 mb-3">Task View</h3>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => {
                    setViewMode('tasks');
                    setStatusFilter('all');
                    setCurrentPage(1);
                  }}
                  className={`px-4 py-2 rounded-md text-sm font-medium ${
                    viewMode === 'tasks'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  All Tasks
                </button>
                <button
                  onClick={() => {
                    setViewMode('tasks');
                    setStatusFilter('pending');
                    setCurrentPage(1);
                  }}
                  className={`px-4 py-2 rounded-md text-sm font-medium ${
                    viewMode === 'tasks' && statusFilter === 'pending'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Pending
                </button>
                <button
                  onClick={() => {
                    setViewMode('tasks');
                    setStatusFilter('resolved');
                    setCurrentPage(1);
                  }}
                  className={`px-4 py-2 rounded-md text-sm font-medium ${
                    viewMode === 'tasks' && statusFilter === 'resolved'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Completed
                </button>
              </div>
            </div>
          </div>
        )}

        {statusUpdateError && (
          <div className="mb-4 rounded-lg bg-red-50 border border-red-200 px-4 py-3 flex justify-between items-center">
            <p className="text-sm text-red-800">{statusUpdateError}</p>
            <button
              type="button"
              onClick={() => setStatusUpdateError(null)}
              className="text-red-600 text-sm font-medium hover:text-red-800"
            >
              Dismiss
            </button>
          </div>
        )}

        <div className="mb-4 overflow-x-auto pb-1" aria-label="Filter complaints by status">
          <div className="flex min-w-max gap-2">
            {statusTabs.map((tab) => (
              <button
                key={tab.value}
                type="button"
                onClick={() => {
                  setStatusFilter(tab.value);
                  setCurrentPage(1);
                }}
                className={`min-h-11 rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${
                  statusFilter === tab.value
                    ? 'border-blue-600 bg-blue-600 text-white shadow-sm'
                    : 'border-gray-200 bg-white text-gray-700 hover:border-blue-300 hover:bg-blue-50'
                }`}
              >
                {tab.label}
                {typeof tab.count === 'number' && (
                  <span className={`ml-2 rounded-full px-2 py-0.5 text-xs ${statusFilter === tab.value ? 'bg-white/20' : 'bg-gray-100'}`}>
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white shadow-sm border border-gray-100 rounded-2xl mb-6">
          <div className="p-4 sm:p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search complaints..."
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                <select
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="assigned">Assigned</option>
                  <option value="in_progress">In Progress</option>
                  <option value="on_hold">On Hold</option>
                  <option value="resolved">Resolved</option>
                  <option value="closed">Closed</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
                <select
                  value={priorityFilter}
                  onChange={(e) => {
                    setPriorityFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Priority</option>
                  <option value="urgent">Urgent</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">From date & time</label>
                <input
                  type="datetime-local"
                  value={startDate}
                  max={endDate || undefined}
                  onChange={(e) => { setStartDate(e.target.value); setCurrentPage(1); }}
                  className="w-full min-h-11 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">To date & time</label>
                <input
                  type="datetime-local"
                  value={endDate}
                  min={startDate || undefined}
                  onChange={(e) => { setEndDate(e.target.value); setCurrentPage(1); }}
                  className="w-full min-h-11 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="flex items-end">
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setStatusFilter('all');
                    setPriorityFilter('all');
                    setStartDate('');
                    setEndDate('');
                    setCurrentPage(1);
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Clear Filters
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Complaints Display */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className={`${mobileClasses.cardMobileSmall} border-b border-gray-200`}>
            <h3 className={`${mobileClasses.textMobileLarge} font-medium text-gray-900`}>
              Complaints ({totalCount})
              {totalCount > itemsPerPage && (
                <span className="text-sm font-normal text-gray-500 ml-2">
                  — showing {(currentPage - 1) * itemsPerPage + 1}–
                  {Math.min(currentPage * itemsPerPage, totalCount)} of {totalCount}
                </span>
              )}
            </h3>
          </div>

          <div className={`${mobileClasses.cardMobile}`}>
            {isMobile() ? (
              <MobileComplaintsTable
                complaints={filteredComplaints}
                isLoading={isLoading}
                onStatusChange={(isAdmin || isDepartmentStaff) ? handleStatusChange : undefined}
                onViewDetails={(complaint) => {
                  setSelectedComplaint(complaint);
                  setShowDetailsModal(true);
                }}
                isAdmin={isAdmin || isDepartmentStaff}
              />
            ) : (
              <>
                {isLoading ? (
                  <div className="flex justify-center items-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  </div>
                ) : filteredComplaints.length === 0 ? (
                  <div className="text-center py-12">
                    <MessageSquare className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900">No complaints found</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      {searchTerm || statusFilter !== 'all' || priorityFilter !== 'all' || startDate || endDate
                        ? 'Try adjusting your filters to see more results.'
                        : 'Get started by creating a new complaint.'}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {filteredComplaints.map((complaint) => renderComplaintCard(complaint))}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Page <span className="font-medium">{currentPage}</span> of{' '}
                    <span className="font-medium">{totalPages}</span>
                    <span className="text-gray-500"> ({totalCount} complaints)</span>
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                      disabled={currentPage === totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                    >
                      Next
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Complaint Details Modal */}
      {showDetailsModal && selectedComplaint && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Complaint Details
                </h3>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <span className="sr-only">Close</span>
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <h4 className="text-sm font-medium text-gray-700">Title</h4>
                  <p className="mt-1 text-sm text-gray-900">{selectedComplaint.title}</p>
                </div>

                <div>
                  <h4 className="text-sm font-medium text-gray-700">Description</h4>
                  <p className="mt-1 text-sm text-gray-900">{selectedComplaint.description}</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h4 className="text-sm font-medium text-gray-700">Status</h4>
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(selectedComplaint.status)}`}>
                      {selectedComplaint.status.replace('_', ' ').toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-700">Priority</h4>
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getPriorityColor(selectedComplaint.priority)}`}>
                      {selectedComplaint.priority.toUpperCase()}
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h4 className="text-sm font-medium text-gray-700">Category</h4>
                    <p className="mt-1 text-sm text-gray-900">{selectedComplaint.category.name}</p>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-700">Created</h4>
                    <p className="mt-1 text-sm text-gray-900">{formatDate(selectedComplaint.created_at)}</p>
                  </div>
                </div>

                <div>
                  <h4 className="text-sm font-medium text-gray-700">Complainant</h4>
                  <div className="mt-1">
                    <p className="text-sm text-gray-900">
                      {selectedComplaint.user.first_name} {selectedComplaint.user.last_name}
                    </p>
                    <p className="text-sm text-gray-500">{selectedComplaint.user.email}</p>
                    <p className="text-sm text-gray-500">{selectedComplaint.user.phone}</p>
                  </div>
                </div>

                {getComplaintPlotLabel(selectedComplaint) && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700">Block / House</h4>
                    <p className="mt-1 text-sm font-medium text-amber-900">{getComplaintPlotLabel(selectedComplaint)}</p>
                  </div>
                )}

                {selectedComplaint.location && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700">Location</h4>
                    <p className="mt-1 text-sm text-gray-900">{selectedComplaint.location}</p>
                  </div>
                )}

                {selectedComplaint.resolution_notes && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700">Resolution Notes</h4>
                    <p className="mt-1 text-sm text-gray-900">{selectedComplaint.resolution_notes}</p>
                  </div>
                )}
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Close
                </button>
                <Link
                  to={`/app/complaints/${selectedComplaint.id}`}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                >
                  View Full Details
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ComplaintsPage;
